import { useTranslation } from 'react-i18next';

import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';
import { ETICHETTA_SCHEDA, RIGA_SCHEDE, SCHEDA_STRETTA } from '@/components/common/schedeSezione';
import type { ComponentType, SVGProps } from 'react';
import type { BackgammonSectionKey } from '@/components/backgammon/lobbySections';

/** Cio' che la riga legge di una scheda: la chiave, l'icona e l'etichetta (`BackgammonSectionDef` ne e' un'estensione). */
export interface SchedaDef<K extends string = string> {
  key: K;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
}

/**
 * La riga di schede della rotta `/backgammon`. Stessa gerarchia e stesso aspetto di quella della
 * Hub (pannello vetro, pastiglia piena sulla voce attiva, icona sopra l'etichetta): le voci NON
 * si scrivono qui, arrivano dall'elenco unico `backgammonSectionDefs`.
 *
 * ⚠️ Niente swipe e niente pastiglia scorrevole: sulla Hub servono alla griglia da otto voci e
 * al banner che si comprime; qui le voci vanno in righe da quattro (dal 24/09 sono piu' di quattro) e il banner non
 * viene guidato da questa riga.
 *
 * Generalizzata (sezione Avatar a schede, I-635): la chiave e' un parametro di tipo, e `ariaLabel`, `testIdPrefix` e `perRiga` hanno come
 * default i valori di sempre — i due posti del backgammon non passano niente e rendono lo stesso DOM (`tests/schede-sezione-generica.test.tsx`).
 * `perRiga` e' la capienza di una riga (default 4, la regola della Hub): chi ne passa una piu' larga lo fa DOPO averla misurata.
 */
export function BackgammonSectionTabs<K extends string = BackgammonSectionKey>({
  sections,
  active,
  onSelect,
  ariaLabel,
  testIdPrefix = 'bg-scheda',
  perRiga = 4,
}: {
  sections: SchedaDef<K>[];
  active: K;
  onSelect: (key: K) => void;
  ariaLabel?: string;
  testIdPrefix?: string;
  perRiga?: number;
}) {
  const { t } = useTranslation('hub');
  const haptics = useHaptics();
  return (
    <div
      role="tablist"
      aria-label={ariaLabel ?? t('sections.play', { defaultValue: 'Play' })}
      // Righe da QUATTRO, come la griglia della Hub (`HubSections`, COLS = 4): con piu' di quattro
      // schede si va a capo invece di stringere le etichette. Fino a quattro resta una riga sola.
      // Righe ESPLICITE (non una griglia unica) dal 04/10: sotto i 360 ogni riga distribuisce lo spazio secondo
      // il testo delle sue schede, e le colonne non possono essere condivise fra le righe (`schedeSezione.ts`).
      className="card-edge flex shrink-0 flex-col gap-1 rounded-2xl bg-bg/55 p-1.5 backdrop-blur-md"
    >
      {Array.from({ length: Math.ceil(sections.length / perRiga) }, (_, r) =>
        sections.slice(r * perRiga, r * perRiga + perRiga),
      ).map((riga, r) => (
        <div
          key={r}
          className={RIGA_SCHEDE}
          style={{ gridTemplateColumns: `repeat(${Math.min(sections.length, perRiga)}, minmax(0, 1fr))` }}
        >
          {riga.map((s) => {
            const on = s.key === active;
            return (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={on}
                data-active={on}
                data-testid={`${testIdPrefix}-${s.key}`}
                onClick={() => {
                  if (on) return;
                  haptics.selection();
                  onSelect(s.key);
                }}
                className={cn(
                  'flex min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-1 py-2 transition-colors active:opacity-80',
                  // Finestre basse (320/360 dentro Telegram): la riga scende da 57 a 44 px (I-334 D). Stessa regola della Hub.
                  'min-h-11 [@media(max-height:640px)]:gap-0.5 [@media(max-height:640px)]:py-1',
                  SCHEDA_STRETTA,
                  on ? 'bg-brand text-brand-fg shadow-lg shadow-brand/20' : 'text-fg-muted',
                )}
              >
                <s.Icon
                  aria-hidden
                  className="h-5 w-5 shrink-0 [@media(max-height:640px)]:h-4 [@media(max-height:640px)]:w-4"
                  strokeWidth={2}
                />
                <span className={ETICHETTA_SCHEDA}>{s.label}</span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
